import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Navbar } from '../components/layout/Navbar';
import { Search, FileText, ArrowRight, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SearchPage() {
    const [query, setQuery] = useState('');
    const [results, setResults] = useState([]);
    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searched, setSearched] = useState(false);

    useEffect(() => {
        fetchProjects();
    }, []);

    useEffect(() => {
        const debounce = setTimeout(() => {
            if (query.length >= 2) {
                searchDocs();
            } else {
                setResults([]);
                setSearched(false);
            }
        }, 300);

        return () => clearTimeout(debounce);
    }, [query]);

    const fetchProjects = async () => {
        try {
            const response = await axios.get(`${API}/projects`);
            setProjects(response.data);
        } catch (error) {
            console.error('Error fetching projects:', error);
        }
    };

    const searchDocs = async () => {
        setLoading(true);
        try {
            const response = await axios.get(`${API}/search?q=${encodeURIComponent(query)}`);
            setResults(response.data);
            setSearched(true);
        } catch (error) {
            console.error('Search error:', error);
        } finally {
            setLoading(false);
        }
    };

    const getProjectByDoc = (doc) => {
        return projects.find(p => p.id === doc.project_id);
    };

    return (
        <div className="min-h-screen bg-background">
            <div className="noise-overlay" />
            <div className="fixed inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-primary/5 via-transparent to-transparent pointer-events-none" />
            
            <Navbar />

            <main className="pt-32 pb-20 px-6">
                <div className="max-w-3xl mx-auto">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5 }}
                        className="text-center mb-12"
                    >
                        <h1 className="text-4xl md:text-5xl font-mono font-bold mb-4">
                            <Search className="w-10 h-10 inline-block text-primary mr-3" />
                            Suche
                        </h1>
                        <p className="text-muted-foreground">
                            Durchsuche alle Dokumentationen nach Titeln, Inhalten und Tags
                        </p>
                    </motion.div>

                    {/* Search Input */}
                    <div className="relative mb-12">
                        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                        <Input
                            type="text"
                            placeholder="Suche nach Scripts, Funktionen, Events..."
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            className="w-full pl-12 pr-4 py-6 text-lg rounded-none border-2 border-border bg-card/50 focus:border-primary font-mono"
                            data-testid="search-input"
                            autoFocus
                        />
                        {loading && (
                            <Loader2 className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 animate-spin text-primary" />
                        )}
                    </div>

                    {/* Results */}
                    {searched && (
                        <div className="space-y-4">
                            {results.length === 0 ? (
                                <div className="text-center py-12 border border-dashed border-border rounded-xl">
                                    <FileText className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                                    <h3 className="font-mono font-bold text-lg mb-2">Keine Ergebnisse</h3>
                                    <p className="text-muted-foreground text-sm">
                                        Versuche andere Suchbegriffe
                                    </p>
                                </div>
                            ) : (
                                <>
                                    <p className="text-sm text-muted-foreground mb-4">
                                        {results.length} Ergebnis{results.length !== 1 ? 'se' : ''} gefunden
                                    </p>
                                    {results.map((doc, index) => {
                                        const project = getProjectByDoc(doc);
                                        return (
                                            <motion.div
                                                key={doc.id}
                                                initial={{ opacity: 0, y: 10 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                transition={{ duration: 0.3, delay: index * 0.05 }}
                                            >
                                                <Link 
                                                    to={project ? `/docs/${project.slug}/${doc.slug}` : '#'}
                                                    className="block"
                                                    data-testid={`search-result-${doc.id}`}
                                                >
                                                    <div className="p-4 rounded-xl border border-border/50 bg-card/30 hover:border-primary/50 hover:bg-card/50 transition-all group">
                                                        <div className="flex items-start justify-between gap-4">
                                                            <div className="flex-1 min-w-0">
                                                                <div className="flex items-center gap-2 mb-2">
                                                                    <FileText className="w-4 h-4 text-primary flex-shrink-0" />
                                                                    <h3 className="font-mono font-bold truncate group-hover:text-primary transition-colors">
                                                                        {doc.title}
                                                                    </h3>
                                                                </div>
                                                                {project && (
                                                                    <p className="text-xs text-muted-foreground mb-2">
                                                                        {project.name} / {doc.category}
                                                                    </p>
                                                                )}
                                                                <p className="text-sm text-muted-foreground line-clamp-2">
                                                                    {doc.content.substring(0, 150)}...
                                                                </p>
                                                                {doc.tags && doc.tags.length > 0 && (
                                                                    <div className="flex flex-wrap gap-1 mt-2">
                                                                        {doc.tags.slice(0, 3).map(tag => (
                                                                            <Badge 
                                                                                key={tag} 
                                                                                variant="outline" 
                                                                                className="text-xs font-mono"
                                                                            >
                                                                                {tag}
                                                                            </Badge>
                                                                        ))}
                                                                    </div>
                                                                )}
                                                            </div>
                                                            <ArrowRight className="w-5 h-5 text-muted-foreground group-hover:text-primary group-hover:translate-x-1 transition-all flex-shrink-0" />
                                                        </div>
                                                    </div>
                                                </Link>
                                            </motion.div>
                                        );
                                    })}
                                </>
                            )}
                        </div>
                    )}

                    {/* Quick links when no search */}
                    {!searched && projects.length > 0 && (
                        <div>
                            <h2 className="text-sm font-mono uppercase tracking-wider text-muted-foreground mb-4">
                                Schnellzugriff auf Projekte
                            </h2>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                {projects.map(project => (
                                    <Link 
                                        key={project.id}
                                        to={`/docs/${project.slug}`}
                                        className="p-4 rounded-xl border border-border/50 bg-card/30 hover:border-primary/50 transition-all group"
                                        data-testid={`quick-link-${project.slug}`}
                                    >
                                        <div className="flex items-center gap-3">
                                            {project.icon ? (
                                                <span className="text-2xl">{project.icon}</span>
                                            ) : (
                                                <div 
                                                    className="w-8 h-8 rounded"
                                                    style={{ backgroundColor: project.color }}
                                                />
                                            )}
                                            <div>
                                                <h3 className="font-mono font-bold group-hover:text-primary transition-colors">
                                                    {project.name}
                                                </h3>
                                                <p className="text-xs text-muted-foreground">
                                                    {project.description.substring(0, 50)}...
                                                </p>
                                            </div>
                                        </div>
                                    </Link>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </main>
        </div>
    );
}
